import { useCallback, useEffect, useState } from "react";
import { Box, Chip, CircularProgress, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router";
import * as Sentry from "@sentry/react";
import { useApi } from "../../hooks/useApi";
import { EVIDENCE_STATUS_LABELS, EvidenceDraftSummary } from "../../types/evidence";

export default function DisputeEvidenceDrawerSection({ disputeId, open }: {
  disputeId: number;
  open: boolean;
}) {
  const { get, loading } = useApi();
  const [draft, setDraft] = useState<EvidenceDraftSummary | null>(null);
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const fetchDraft = useCallback(async () => {
    setError(false);
    try {
      const res = await get<{ draft: EvidenceDraftSummary | null }>(`/api/disputes/${disputeId}/evidence-drafts/active`);
      setDraft(res.draft);
      setLoaded(true);
    } catch (err) {
      Sentry.captureException(err);
      setError(true);
    }
  }, [get, disputeId]);

  useEffect(() => {
    if (open) fetchDraft();
  }, [open, fetchDraft]);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {loading && !loaded && <CircularProgress size={18} />}
      {error && (
        <Typography variant="body2" color="error">Couldn&apos;t load the evidence status.</Typography>
      )}
      {loaded && !error && (
        draft
          ? <Chip size="small" label={EVIDENCE_STATUS_LABELS[draft.status]} />
          : <Typography variant="body2" color="text.secondary">No draft yet.</Typography>
      )}
      <Link component={RouterLink} to={`/disputes/${disputeId}/evidence`} variant="body2" sx={{ fontWeight: 500 }}>
        Open evidence
      </Link>
    </Box>
  );
}
